// The Oase token: exchanged from an Azure token and refreshed when fewer
// than 10 minutes are left (ADR 0008; portal src/utils/oaseAuth.ts and
// src/hooks/useOaseAuth.ts).
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { jwtDecode } from 'jwt-decode'
import type { HttpAuth } from '../api/types'
import { AuthExpiredError, SessionExpiredError } from './errors'
import { getAccount, getMsal, tokenRequest } from './msal'
import { getSession, setToken } from './session'
import { signOut } from './signOut'

/** Refresh when fewer than this many seconds are left (the portal's 10 minutes). */
export const REFRESH_MARGIN_SECONDS = 10 * 60

/** POST the Azure token to the authorize URL; resolves to the Oase token. */
export async function exchange(azureToken: string): Promise<string> {
  const url = import.meta.env.APP_OAP_AUTHORIZE_URL
  if (!url) throw new Error('OASE authorize URL is not configured')
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ azureToken })
  })
  const data = await readJson(res)
  if (!res.ok) {
    if (data?.message === 'Azure token is expired.') throw new AuthExpiredError()
    throw new Error(
      typeof data?.message === 'string' && data.message ? data.message : 'OASE authorization failed'
    )
  }
  const token = data?.oaseAccessToken
  if (typeof token !== 'string' || !token) throw new Error('OASE authorization failed')
  return token
}

async function readJson(
  res: Response
): Promise<{ message?: unknown; oaseAccessToken?: unknown } | null> {
  try {
    const body: unknown = await res.json()
    return typeof body === 'object' ? body : null
  } catch {
    return null
  }
}

/** Seconds until `exp`, or -Infinity when the token has no readable `exp`. */
export function secondsLeft(token: string, now = Date.now()): number {
  try {
    const { exp } = jwtDecode<{ exp?: unknown }>(token)
    return typeof exp === 'number' ? exp - Math.floor(now / 1000) : -Infinity
  } catch {
    return -Infinity
  }
}

let inFlight: Promise<string> | null = null

/**
 * The session's Oase token, refreshed first when fewer than 10 minutes are
 * left. Concurrent callers share one refresh. Throws SessionExpiredError when
 * there is no token or the refresh fails.
 */
export function ensureFreshToken(): Promise<string> {
  const { token } = getSession()
  if (!token) return Promise.reject(new SessionExpiredError('Not signed in'))
  if (secondsLeft(token) >= REFRESH_MARGIN_SECONDS) return Promise.resolve(token)
  inFlight ??= refresh().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function acquireAndExchange(forceRefresh: boolean): Promise<string> {
  const account = getAccount()
  if (!account) throw new SessionExpiredError('No MSAL account')
  const { accessToken } = await getMsal().acquireTokenSilent({
    ...tokenRequest,
    account,
    ...(forceRefresh && { forceRefresh: true })
  })
  const token = await exchange(accessToken)
  setToken(token)
  return token
}

const shouldRetry = (error: unknown) =>
  error instanceof AuthExpiredError || error instanceof InteractionRequiredAuthError

async function refresh(): Promise<string> {
  try {
    try {
      return await acquireAndExchange(false)
    } catch (error) {
      if (!shouldRetry(error)) throw error
      return await acquireAndExchange(true)
    }
  } catch (error) {
    if (error instanceof SessionExpiredError) throw error
    throw new SessionExpiredError('Could not refresh the Oase token', { cause: error })
  }
}

/** Is there an MSAL account in the shared cache (false before MSAL is initialised)? */
export function hasCachedAccount(): boolean {
  try {
    return getAccount() !== null
  } catch {
    return false
  }
}

let restoring: Promise<string | null> | null = null

/**
 * The portal's checkAuth (useAuthProvider.ts:116-125, useOaseAuth.ts:11-16):
 * with a cached MSAL account but no Oase token, get an Azure token silently
 * and exchange it, so no sign-in popup is needed. Resolves to the new token,
 * or null when it cannot (no account, interaction required, exchange
 * refused). It never signs out: the caller decides what null means.
 */
export function restoreSilently(): Promise<string | null> {
  if (getSession().token) return Promise.resolve(getSession().token)
  if (!hasCachedAccount()) return Promise.resolve(null)
  restoring ??= refresh()
    .catch(() => null)
    .finally(() => {
      restoring = null
    })
  return restoring
}

/** What src/api uses to authenticate requests (src/api/types.ts). */
export const httpAuth: HttpAuth = {
  async getToken() {
    if (!getSession().token) return null
    try {
      return await ensureFreshToken()
    } catch (error) {
      if (error instanceof SessionExpiredError) void signOut({ reason: 'expired' })
      throw error
    }
  },
  onUnauthorized() {
    void signOut({ reason: 'expired' })
  }
}
